import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { CheckCircle2, FileWarning, Upload, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const DOC_LABELS = {
  license_front: "Driver's License — Front",
  license_back: "Driver's License — Back",
  instructor_cert: "Instructor Certification",
  vehicle_insurance: "Vehicle Insurance",
  criminal_consent: "Criminal Record Consent",
  selfie: "Selfie / ID Verification",
  gov_id: "Government Photo ID",
};

const DOC_HINTS = {
  gov_id: "Driver's licence, learner permit, passport or provincial photo card — any one is fine.",
  selfie: "A clear photo of your face, taken now — used only to match your ID.",
};

const AUDIENCE = {
  trainer: {
    basePath: "/trainer/kyc",
    heading: "Identity & Compliance (KYC)",
    intro: "Upload the 6 documents below. An admin will review within 2 business days. PIPEDA-compliant: only you and DriveTerrain admin can view these files.",
  },
  learner: {
    heading: "Identity Verification",
    basePath: "/learner/kyc",
    intro: "Upload one government photo ID and a selfie. An admin reviews these within 2 business days. You can keep booking lessons while this is pending — verified learners simply get a badge instructors can see. PIPEDA-compliant: only you and DriveTerrain admin can view these files.",
  },
};

export default function KycPanel({ audience = "trainer" }) {
  const cfg = AUDIENCE[audience];
  const [data, setData] = useState({ documents: [], required: [], kyc_status: "incomplete" });
  const [uploadingType, setUploadingType] = useState("");

  const load = () => api.get(cfg.basePath).then(r => setData(r.data));
  // Reload when the panel is pointed at a different role's KYC endpoint.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cfg.basePath]);

  const docByType = (t) => data.documents.find(d => d.doc_type === t);

  const upload = async (e, doc_type) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Client-side 1MB pre-check so users don't wait for a slow upload only
    // to see the server reject it. Server enforces the same limit as the
    // source of truth.
    const ONE_MB = 1 * 1024 * 1024;
    if (f.size > ONE_MB) {
      toast.error(`File too large — max 1 MB per document. This file is ${(f.size / 1024 / 1024).toFixed(2)} MB.`);
      e.target.value = "";
      return;
    }
    setUploadingType(doc_type);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`${cfg.basePath}/upload?doc_type=${doc_type}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${DOC_LABELS[doc_type]} uploaded`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setUploadingType("");
    e.target.value = "";
  };

  const StatusBadge = () => {
    if (data.kyc_status === "verified") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-bold border border-primary bg-primary/5 text-primary" data-testid="kyc-verified-badge">
          <ShieldCheck className="w-4 h-4" /> Verified
        </span>
      );
    }
    if (data.kyc_status === "pending") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-bold border border-border bg-card text-muted-foreground" data-testid="kyc-pending-badge">
          <Clock className="w-4 h-4" /> Under review
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-bold border border-destructive/40 bg-destructive/5 text-destructive" data-testid="kyc-incomplete-badge">
        <FileWarning className="w-4 h-4" /> {data.kyc_status === "rejected" ? "Action needed" : "Incomplete"}
      </span>
    );
  };

  return (
    <div className="space-y-5" data-testid="kyc-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="font-display text-xl font-medium">{cfg.heading}</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">{cfg.intro}</p>
        </div>
        <StatusBadge />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {data.required.map(t => {
          const d = docByType(t);
          const isUploading = uploadingType === t;
          return (
            <div key={t} className="border border-border bg-card p-4 flex flex-col gap-2" data-testid={`kyc-card-${t}`}>
              <div className="text-sm font-medium">{DOC_LABELS[t] || t}</div>
              {DOC_HINTS[t] && <div className="text-xs text-muted-foreground">{DOC_HINTS[t]}</div>}
              {d ? (
                <div className="flex flex-wrap items-center gap-2">
                  {d.review_status === "approved" && <span className="text-xs px-2 py-0.5 border border-primary text-primary uppercase tracking-widest" data-testid={`kyc-status-${t}`}><CheckCircle2 className="w-3 h-3 inline mr-1" /> Approved</span>}
                  {d.review_status === "pending" && <span className="text-xs px-2 py-0.5 border border-border uppercase tracking-widest" data-testid={`kyc-status-${t}`}>Pending review</span>}
                  {d.review_status === "rejected" && <span className="text-xs px-2 py-0.5 border border-destructive text-destructive uppercase tracking-widest" data-testid={`kyc-status-${t}`}>Rejected{d.reject_reason ? `: ${d.reject_reason}` : ""}</span>}
                  <span className="text-xs text-muted-foreground truncate">{d.original_filename}</span>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">No file uploaded yet.</div>
              )}
              <label className="block mt-1">
                <input type="file" accept="image/*,application/pdf" className="hidden"
                       onChange={(e) => upload(e, t)}
                       disabled={isUploading || (d && d.review_status !== "rejected")}
                       data-testid={`kyc-file-${t}`} />
                {d && d.review_status !== "rejected" ? (
                  <span className="text-xs text-muted-foreground italic" data-testid={`kyc-locked-${t}`}>
                    Submitted — one-time upload
                  </span>
                ) : (
                  <Button type="button" variant="outline" className="rounded-sm h-9 w-full" asChild disabled={isUploading}>
                    <span><Upload className="w-3.5 h-3.5 mr-1.5" /> {d ? "Re-upload after rejection" : "Upload"}{isUploading ? "…" : ""}</span>
                  </Button>
                )}
              </label>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">Accepted: JPG, PNG, WEBP, PDF · up to <strong>1 MB</strong> each · <strong>one-time submission</strong> per document (re-upload only allowed if rejected).</p>
    </div>
  );
}
